/**
 * Typed URL path builders for the SPA.
 *
 * ## What this is (and is NOT)
 *
 * This module is about **URL construction** — turning `/residents/:id` + an id
 * into `/residents/abc123` — so call-sites stop hand-writing template-literal
 * path strings (`navigate(`/residents/${id}`)`). It gives one greppable catalog
 * of every navigable path and compile-time-checked params.
 *
 * It is **not** the US-050 "declarative resource routing" mechanism. That work
 * (US-047 epic) is about *authorization* — declaring which `resource`/permission
 * a route gates — and lives in `router/` + `RouteGuard` + `decide.ts`. The two
 * are orthogonal: this file never reads `RouteAccess.access`/`resource`, only the
 * URL shape. Keep them separate; do not fold authz concerns in here.
 *
 * ## Single source of truth (no drift)
 *
 * The route *structure* is owned by the portal tables (`router/portals/*.ts`).
 * This catalog mirrors the paths that are linked across the app, and
 * `routePaths.test.ts` walks the real portal trees and fails the build if a
 * builder here points at a route that no longer exists, or if a new route lands
 * without a builder (or an explicit opt-out). Rename a segment in a portal file
 * → the test goes red until this catalog matches. That reconciliation is what
 * makes the catalog "derived" rather than a second list that silently rots.
 *
 * ## Usage
 *
 * ```ts
 * import { routePaths } from "@/lib/routePaths";
 *
 * // path params (compile error if you omit/misspell `id`):
 * navigate(routePaths.residentDetail({ id: resident.id }));
 *
 * // path params + query (use the real Tabs.Tab slug, e.g. "care-plan"):
 * navigate(routePaths.residentDetail({ id }, { tab: "care-plan" }));
 * //   → /residents/abc123?tab=care-plan
 *
 * // no path params — query only:
 * // multi-param:
 * <Link to={routePaths.auditFacilityResident({ facilityId, residentId })}>…</Link>
 * ```
 *
 * Migration is opt-in: existing `navigate("/literal")` sites keep working; reach
 * for `routePaths.*` in new code and when you touch an old call-site.
 */

// ── Catalog ──────────────────────────────────────────────────────────────────
// One entry per linkable route, grouped to match `router/portals/*.ts`. Settings
// sub-tabs are intentionally omitted (reached via the settings page's own tab UI,
// often with `?tab=`); they live on the test's INTENTIONALLY_UNBUILT allowlist.
export const ROUTE_TEMPLATES = {
  // App portal (base "/") ─────────────────────────────────────────────────────
  supervisedSession:
    "/external-providers/:providerId/assignments/:assignmentId/supervised",
  dashboard: "/dashboard",
  residents: "/residents",
  residentNew: "/residents/new",
  residentEdit: "/residents/:id/edit",
  residentDetail: "/residents/:id",
  residentFormRequirementDetail: "/residents/:id/form-requirements/:reqId",
  // Platform requests (form-template asks), reached from the app header launcher.
  requests: "/requests",
  facilities: "/facilities",
  facilityDetail: "/facilities/:id",
  // Org-wide (cross-facility) roll-up, reached from the Facilities page header.
  groupHome: "/group-home",
  // US-144 — billing documentation for a facility + period. Reached from the
  // header launcher (cross-facility) and the Facilities kebab, which appends
  // `?facility=<id>` to preselect. Not in the sidebar.
  billingPack: "/billing-pack",
  employees: "/employees",
  employeeDetail: "/employees/:id",
  incidents: "/incidents",
  incidentDetail: "/incidents/:id",
  medicationsDue: "/medications-due",
  medicationReview: "/medication-review",
  carePlans: "/care-plans",
  myTasks: "/my-tasks",
  taskCompletion: "/task-completion",
  calendar: "/calendar",
  cases: "/cases",
  caseDetail: "/cases/:id",
  forms: "/forms",
  formPreview: "/forms/:id/preview",
  formSubmit: "/forms/:templateId/submit",
  formSubmissions: "/forms/submissions",
  formSubmissionDetail: "/forms/submissions/:id",
  auditManagement: "/audit-management",
  training: "/training",
  trainingMaterial: "/training/:materialId",
  trainingAssessment: "/training/:materialId/assessment",
  trainingAssign: "/training/assign",
  // US-032/US-035 group-home config consolidated into Settings tabs. Only the
  // Form Rules tab is a cross-component link target (from the rule-library
  // import flow); the rule-library tab + the legacy /group-home/* redirects are
  // opted out in the drift test.
  settingsAppointmentFormRules: "/settings/appointment-form-rules",
  settings: "/settings",
  // Personal account page (Profile + My Schedule + Notifications tabs), reached
  // from each portal's header account menu. One per portal base.
  profile: "/profile",
  // Notifications feed — the bell dropdown's "View all notifications" target.
  // App portal only (the bell renders in AppLayout).
  notifications: "/notifications",
  facilityWebhooks: "/facility-settings/webhooks",
  facilityWebhookDeliveries: "/facility-settings/webhooks/:id/deliveries",

  // Admin portal (base "/admin") ──────────────────────────────────────────────
  admin: "/admin",
  adminUsers: "/admin/users",
  adminGroupHomes: "/admin/group-homes",
  adminGroupHomeDetail: "/admin/group-homes/:id",
  adminFacilityPharmacyIntegration:
    "/admin/facilities/:facilityId/pharmacy-integration",
  adminPharmacyPartners: "/admin/pharmacy-partners",
  adminAuditLogs: "/admin/audit-logs",
  adminSubscriptions: "/admin/subscriptions",
  adminAuditorGroups: "/admin/auditor-groups",
  adminAuditorGroupDetail: "/admin/auditor-groups/:id",
  adminForms: "/admin/forms",
  adminFormNew: "/admin/forms/new",
  adminFormDetail: "/admin/forms/:id",
  adminFormEdit: "/admin/forms/:id/edit",
  adminFormVersions: "/admin/forms/:id/versions",
  adminRequests: "/admin/requests",
  adminRuleLibrary: "/admin/rule-library",
  adminRuleLibraryDetail: "/admin/rule-library/:id",
  adminTraining: "/admin/training",
  adminTrainingNew: "/admin/training/new",
  adminTrainingEdit: "/admin/training/:id/edit",
  adminSystem: "/admin/system",
  adminSettings: "/admin/settings",
  adminProfile: "/admin/profile",

  // Audit portal (base "/audit") ──────────────────────────────────────────────
  audit: "/audit",
  // F-audit-access-consolidation: "Browse Facilities" (was /audit/group-homes)
  // and "Group Home Access" (was /audit/my-requests) folded into one tabbed
  // page; `?tab=` distinguishes them (like auditAuditors' `?tab=`, below).
  // The old paths still exist as portal-tree redirects (bookmark safety) but
  // deliberately have no builder here, same precedent as `/audit-findings`.
  auditAccess: "/audit/access",
  auditGroupHomeDetail: "/audit/group-homes/:groupHomeId",
  auditRequestNew: "/audit/requests/new",
  auditFacility: "/audit/facilities/:facilityId",
  auditSessions: "/audit/sessions",
  auditSession: "/audit/sessions/:sessionId",
  auditFacilityResident: "/audit/facilities/:facilityId/residents/:residentId",
  auditFacilityIncident: "/audit/facilities/:facilityId/incidents/:incidentId",
  auditFacilityCarePlan: "/audit/facilities/:facilityId/care-plans/:planId",
  auditFacilityFindings: "/audit/facilities/:facilityId/findings",
  auditFacilityHistory: "/audit/facilities/:facilityId/history",
  auditFacilityEmergencyContacts:
    "/audit/facilities/:facilityId/emergency-contacts",
  auditFacilityPhysicianCompleteness:
    "/audit/facilities/:facilityId/physician-completeness",
  auditFacilityVitalSigns:
    "/audit/facilities/:facilityId/vital-signs-compliance",
  auditFacilityAdlCompliance: "/audit/facilities/:facilityId/adl-compliance",
  auditFacilityCareNotes: "/audit/facilities/:facilityId/care-notes-analysis",
  auditFacilityAppointments:
    "/audit/facilities/:facilityId/appointments-tracking",
  auditFacilityPharmacyInsurance:
    "/audit/facilities/:facilityId/pharmacy-insurance-completeness",
  // F-audit-portal-ia — the auditor-group-admin surface (group overview +
  // members roster + invitation requests). Sub-tab is a `?tab=` query param
  // (like residentDetail's `?tab=`), not a nested path template.
  auditAuditors: "/audit/auditors",
  auditInviteAuditors: "/audit/invite-auditors",
  auditSettings: "/audit/settings",
  auditProfile: "/audit/profile",
} as const;

// ── Param extraction (compile-time) ──────────────────────────────────────────
/** Union of `:param` names in a path template (`"/a/:x/b/:y"` → `"x" | "y"`). */
type PathParamNames<T extends string> =
  T extends `${string}:${infer Param}/${infer Rest}`
    ? Param | PathParamNames<`/${Rest}`>
    : T extends `${string}:${infer Param}`
      ? Param
      : never;

/** `{ [param]: string }` for a template, or an empty object when it has none. */
export type PathParams<T extends string> = [PathParamNames<T>] extends [never]
  ? Record<never, never>
  : { [K in PathParamNames<T>]: string };

/** Query-string values; nullish entries are dropped. */
export type QueryParams = Record<
  string,
  string | number | boolean | null | undefined
>;

/**
 * Builder signature for one template: it requires a params object iff the path
 * has `:params`, and always accepts an optional query object.
 */
type Builder<T extends string> = [PathParamNames<T>] extends [never]
  ? (query?: QueryParams) => string
  : (params: PathParams<T>, query?: QueryParams) => string;

type RoutePathBuilders = {
  [K in keyof typeof ROUTE_TEMPLATES]: Builder<(typeof ROUTE_TEMPLATES)[K]>;
};

// ── Runtime substitution ─────────────────────────────────────────────────────
const PARAM_RE = /:([A-Za-z0-9_]+)/g;

function applyParams(template: string, params: Record<string, string>): string {
  return template.replace(PARAM_RE, (_match, name: string) => {
    const value = params[name];
    if (value == null) {
      throw new Error(`Missing route param ":${name}" for "${template}"`);
    }
    return encodeURIComponent(value);
  });
}

function applyQuery(path: string, query?: QueryParams): string {
  if (!query) return path;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value == null) continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

function makeBuilder(template: string): (...args: never[]) => string {
  const hasParams = new RegExp(PARAM_RE.source).test(template);
  if (hasParams) {
    return ((params: Record<string, string>, query?: QueryParams) =>
      applyQuery(applyParams(template, params), query)) as never;
  }
  return ((query?: QueryParams) => applyQuery(template, query)) as never;
}

/**
 * Named, type-safe path builders. Keys mirror {@link ROUTE_TEMPLATES}; each
 * value is a function whose params are derived from that template's `:segments`.
 */
export const routePaths = Object.fromEntries(
  Object.entries(ROUTE_TEMPLATES).map(([name, template]) => [
    name,
    makeBuilder(template),
  ]),
) as RoutePathBuilders;
